/**
 * "Auswahl benachrichtigen" dialog.
 *
 * `POST /api/dashboard/notify` takes the operator's selection of
 * ProduktInstanzen, records one Benachrichtigung per KEV-affected
 * KomponenteVersion and instance (idempotent per pair) and returns ONE drafted
 * German advisory per Kunde — covering only the selected instances of that
 * customer. It never sends anything, which is why every advisory offers a
 * `mailto:` link the operator opens in their own mail client.
 *
 * The notify payload carries no `mailto` (unlike the triage e-mail drafts), so it
 * is assembled from Empfänger/Betreff/Inhalt here.
 *
 * "Als versendet markieren" stamps the still-OFFEN Benachrichtigungen of the
 * advisory's instances. The ids come from the LIVE overview payload
 * (`openIdsFor`), not from the notify response — the response says which cards
 * were touched, not which of them are still open, and the fan-out of a
 * BETROFFEN verdict may have created further ones.
 */

import { useCallback, useState, type ReactNode } from 'react';

import { CveText } from '../../components/CveLink';
import Banner from '../../components/Banner';
import { Icon } from '../../components/Icon';
import Modal from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { errorMessage, markBenachrichtigungVersendet } from '../../lib/api';
import { countLabel } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type { NotifyResult } from '../../lib/types';
import { buildMailto, orFallback } from './dashboardModel';

export interface NotifyDialogProps {
  /** German context line for the dialog subtitle, e.g. "3 Produkte · 2 Kunden". */
  label: string;
  /** True while the notify request is in flight. */
  busy: boolean;
  /** One advisory per Kunde; null until the request returns. */
  results: readonly NotifyResult[] | null;
  /** Verbatim German server message of a failed notify call. */
  error: string | null;
  /** Benachrichtigung ids of these instances that are still OFFEN. */
  openIdsFor: (produktIds: readonly number[]) => number[];
  /** Display name of an instance for the recorded-cards list. */
  instanceName: (produktId: number | null) => string;
  /** `notification.send` — required by both endpoints used here. */
  canNotify: boolean;
  onClose: () => void;
  /** Called after a successful stamp so the page can reload both payloads. */
  onMarked: () => void;
}

interface AdvisoryProps {
  result: NotifyResult;
  openIds: readonly number[];
  instanceName: (produktId: number | null) => string;
  canNotify: boolean;
  /** Several advisories — show the customer as a section heading. */
  withHeading: boolean;
  onMarked: () => void;
  onBusy: (busy: boolean) => void;
}

/** One customer's advisory with its own mail link and stamp action. */
function Advisory({
  result,
  openIds,
  instanceName,
  canNotify,
  withHeading,
  onMarked,
  onBusy,
}: AdvisoryProps): ReactNode {
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);
  const [marked, setMarked] = useState(0);

  const onMark = useCallback(async (): Promise<void> => {
    setMarking(true);
    onBusy(true);
    setMarkError(null);
    const failures: string[] = [];
    let done = 0;
    for (const id of openIds) {
      try {
        // Sequential on purpose: each call PUTs a CMDBuild card, and a partial
        // failure must name the id that failed instead of racing.
        await markBenachrichtigungVersendet(id, { empfaenger: result.empfaenger ?? '' });
        done += 1;
      } catch (err) {
        failures.push(`#${id}: ${errorMessage(err)}`);
      }
    }
    setMarked(done);
    setMarking(false);
    onBusy(false);
    if (failures.length > 0) setMarkError(failures.join(' · '));
    if (done > 0) onMarked();
  }, [openIds, result, onMarked, onBusy]);

  const mailto = buildMailto(result.empfaenger, result.betreff, result.inhalt);
  const multiProduct = result.produkt_ids.length > 1;

  return (
    <section className="dash-advisory">
      {withHeading ? (
        <h3>
          {orFallback(result.kunde_name, 'Unbekannter Kunde')}
          <span className="muted">
            {' '}
            · {countLabel(result.produkt_ids.length, 'Produkt', 'Produkte')}
          </span>
        </h3>
      ) : null}

      {result.errors.length > 0 ? (
        <Banner kind="error" items={result.errors}>
          Der Server meldet Probleme:
        </Banner>
      ) : null}

      {markError !== null ? (
        <Banner kind="error">Nicht alle Benachrichtigungen konnten gestempelt werden: {markError}</Banner>
      ) : null}
      {marked > 0 && markError === null ? (
        <Banner kind="ok">
          {countLabel(marked, 'Benachrichtigung', 'Benachrichtigungen')} als versendet gestempelt.
        </Banner>
      ) : null}

      {result.meldungen.length > 0 ? (
        <Banner
          kind="info"
          items={result.meldungen.map((meldung) => (
            <CveText
              text={`Benachrichtigung #${meldung.meldung_id ?? '?'} · ${
                multiProduct ? `${instanceName(meldung.produkt_id)} · ` : ''
              }${orFallback(meldung.komponente, 'Komponente')}${
                meldung.cves.length > 0 ? `: ${meldung.cves.join(', ')}` : ''
              }${meldung.created ? ' (neu)' : ' (vorhanden)'}`}
            />
          ))}
        >
          {countLabel(result.meldungen.length, 'Benachrichtigung erfasst', 'Benachrichtigungen erfasst')}
          {openIds.length === 0 ? ', alle als versendet gestempelt.' : '.'}
        </Banner>
      ) : (
        <Banner kind="warn">Keine Benachrichtigung erstellt.</Banner>
      )}

      {result.betreff !== '' ? (
        <div className="mail">
          <div className="to">
            An: {orFallback(result.empfaenger, '(keine E-Mail hinterlegt)')} · Kunde:{' '}
            {orFallback(result.kunde_name, 'unbekannt')}
          </div>
          <div className="subj">{result.betreff}</div>
          <pre>{result.inhalt}</pre>
        </div>
      ) : (
        <div className="muted">
          Kein Anschreiben erzeugt: Die Auswahl enthält keine KEV-betroffene Komponente.
        </div>
      )}

      {mailto !== null || openIds.length > 0 ? (
        <div className="dash-advisory-actions">
          {mailto !== null ? (
            <a
              className="dash-linkbtn"
              href={mailto}
              title="Entwurf im Mailprogramm öffnen (kein Versand durch AssetRadar)"
            >
              <Icon name="mail" /> Im Mailprogramm öffnen
            </a>
          ) : null}
          {openIds.length > 0 ? (
            <button
              type="button"
              className="primary"
              disabled={!canNotify || marking}
              title={canNotify ? undefined : missingPermissionTitle('notification.send')}
              onClick={() => {
                void onMark();
              }}
            >
              {marking ? <Spinner variant="on-accent" /> : null} Als versendet markieren (
              {openIds.length})
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export function NotifyDialog({
  label,
  busy,
  results,
  error,
  openIdsFor,
  instanceName,
  canNotify,
  onClose,
  onMarked,
}: NotifyDialogProps): ReactNode {
  const [marking, setMarking] = useState(0);
  const onBusy = useCallback((next: boolean) => setMarking((count) => count + (next ? 1 : -1)), []);

  const many = results !== null && results.length > 1;

  return (
    <Modal
      title={many ? 'Kundenbenachrichtigungen' : 'Kundenbenachrichtigung'}
      subtitle={`${label} · als Benachrichtigung erfasst (kein realer Versand)`}
      size="wide"
      dismissable={!busy && marking === 0}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="ghost" onClick={onClose} disabled={busy || marking > 0}>
            Schließen
          </button>
        </>
      }
    >
      {busy && results === null ? (
        <div className="row">
          <Spinner /> <span className="muted">Benachrichtigungen werden erfasst…</span>
        </div>
      ) : null}

      {error !== null ? <Banner kind="error">{error}</Banner> : null}

      {many ? (
        <Banner kind="info">
          {countLabel(results.length, 'Anschreiben', 'Anschreiben')}, eines je Kunde mit den
          ausgewählten Produkten.
        </Banner>
      ) : null}

      {results?.map((result) => (
        <Advisory
          key={result.produkt_ids.join('-')}
          result={result}
          openIds={openIdsFor(result.produkt_ids)}
          instanceName={instanceName}
          canNotify={canNotify}
          withHeading={many}
          onMarked={onMarked}
          onBusy={onBusy}
        />
      ))}
    </Modal>
  );
}

export default NotifyDialog;
